import React, { useState } from 'react';
import { Download, X, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { AppIcon } from './AppIcon';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ezvi.app';
const APP_STORE_URL = 'https://apps.apple.com/it/app/ezvi/id6758986699';

export const CTASection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [platform, setPlatform] = useState(null); // 'ios' or 'android'

  const handleDownloadClick = (e, selectedPlatform) => {
    e.preventDefault();
    // Track Google Ads conversion
    gtag_report_conversion();
    if (selectedPlatform === 'ios') {
      window.location.href = APP_STORE_URL;
      return;
    }
    setPlatform(selectedPlatform);
    setIsModalOpen(true);
    setIsSubmitted(false);
    setEmail('');
    setError(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setIsSubmitted(false);
      setEmail('');
      setError(null);
      setPlatform(null);
    }, 300);
  };

  const gtag_report_conversion = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17776709434/qFLxCJP_8fEbELqezJxC',
        'transport_type': 'beacon' // delivered even if the browser switches tabs/navigates
      });
    }
  };

  // Users go straight to the store; still track the conversion
  const handleStoreClick = () => {
    gtag_report_conversion();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('https://www.ezvi.it/api/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        mode: 'cors',  // Explicitly set CORS mode
        credentials: 'omit',  // Don't send cookies
        body: JSON.stringify({
          email: email,
          platform: platform  // 'ios' or 'android'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setIsSubmitting(false);
      setIsSubmitted(true);

    } catch (err) {
      console.error('Error submitting email:', err);
      setError('Si è verificato un errore. Riprova più tardi.');
      setIsSubmitting(false);
    }
  };

  return (
      <>
        <section id="download" className="py-20 md:py-32 relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#8FEC78]/15 via-[#81DD67]/10 to-transparent" />

          {/* Decorative elements */}
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-[#8FEC78]/20 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-[#81DD67]/15 blur-3xl" />

          <div className="container relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-[rgba(0,0,0,0.05)] p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                  {/* App Icon */}
                  <div className="flex-shrink-0">
                    <AppIcon size={120} className="shadow-2xl shadow-[#8FEC78]/40" />
                  </div>

                  {/* Content */}
                  <div className="text-center md:text-left flex-1">
                    <h2 className="heading-2 mb-3">
                      Inizia a risparmiare<br />
                      <span className="text-[rgb(13,121,22)]">oggi stesso</span>
                    </h2>
                    <p className="body-large text-[rgb(131,146,140)] mb-6">
                      Scarica EZVI' gratuitamente e scopri quanto puoi risparmiare sulle tue ricariche.
                      Disponibile per iOS e Android.
                    </p>

                    {/* Download Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3">
                      <a
                          href={APP_STORE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleStoreClick}
                          className="btn-primary flex items-center justify-center gap-2 min-w-[180px]"
                      >
                        <Download size={20} />
                        <span>App Store</span>
                      </a>
                      <a
                          href={PLAY_STORE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleStoreClick}
                          className="btn-primary flex items-center justify-center gap-2 min-w-[180px]"
                      >
                        <Download size={20} />
                        <span>Play Store</span>
                      </a>
                    </div>

                    {/* Trust Badge */}
                    <p className="mt-6 text-sm text-[rgb(175,183,180)]">
                      Gratuita • Nessuna registrazione richiesta • Nessun dato venduto
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coming Soon Modal */}
        {isModalOpen && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
                onClick={handleCloseModal}
            >
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

              {/* Modal */}
              <div
                  className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-scale-in"
                  onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                    onClick={handleCloseModal}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
                    aria-label="Chiudi"
                >
                  <X size={20} className="text-gray-600" />
                </button>

                {!isSubmitted ? (
                    <>
                      {/* Icon */}
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center mb-6 mx-auto">
                        <Download size={32} className="text-white" />
                      </div>

                      {/* Content */}
                      <h3 className="text-2xl md:text-3xl font-bold text-[rgb(0,55,32)] mb-3 text-center">
                        Coming Soon!
                      </h3>
                      <p className="text-[rgb(131,146,140)] mb-6 text-center">
                        EZVI' sarà disponibile presto su {platform === 'ios' ? 'App Store' : 'Play Store'}.
                        Lascia la tua email per essere avvisato non appena lanceremo l'app!
                      </p>

                      {/* Form */}
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="relative">
                          <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[rgb(131,146,140)]" />
                          <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="Il tuo indirizzo email"
                              required
                              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d7916] focus:outline-none transition-colors text-[rgb(0,55,32)]"
                          />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                              <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                              <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Invio in corso...</span>
                              </>
                          ) : (
                              <>
                                <Mail size={20} />
                                <span>Avvisami al lancio</span>
                              </>
                          )}
                        </button>
                      </form>

                      <p className="mt-4 text-xs text-[rgb(175,183,180)] text-center">
                        Non condivideremo mai la tua email con terze parti.
                      </p>
                    </>
                ) : (
                    <>
                      {/* Success State */}
                      <div className="w-16 h-16 rounded-full bg-[#0d7916] flex items-center justify-center mb-6 mx-auto animate-bounce-in">
                        <CheckCircle size={32} className="text-white" />
                      </div>

                      <h3 className="text-2xl md:text-3xl font-bold text-[rgb(0,55,32)] mb-3 text-center">
                        Perfetto!
                      </h3>
                      <p className="text-[rgb(131,146,140)] mb-6 text-center">
                        Grazie per il tuo interesse! Ti invieremo una notifica appena EZVI' sarà disponibile per il download su {platform === 'ios' ? 'App Store' : 'Play Store'}.
                      </p>

                      <button
                          onClick={handleCloseModal}
                          className="w-full btn-primary"
                      >
                        Chiudi
                      </button>
                    </>
                )}
              </div>
            </div>
        )}

        <style jsx>{`
          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes scale-in {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes bounce-in {
            0% {
              opacity: 0;
              transform: scale(0.3);
            }
            50% {
              transform: scale(1.05);
            }
            70% {
              transform: scale(0.9);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          .animate-fade-in {
            animation: fade-in 0.2s ease-out;
          }

          .animate-scale-in {
            animation: scale-in 0.3s ease-out;
          }

          .animate-bounce-in {
            animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }
        `}</style>
      </>
  );
};

export default CTASection;